import { useEffect, useState } from "react";
import {
  Modal,
  Spin,
  Form,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Button,
} from "antd";
import dayjs from "dayjs";

import { postAPI, displayMessage } from "../../../utils/common";
import { EDIT_VENDOR, VENDOR_BY_ID } from "../../../constants/api";
import {
  YES_NO_OPTION,
  colProps,
  ACTIVE_INACTIVE_OPTION,
  urlRegex,
  isSuperAdmin,
} from "../../../constants/constent";
import { ERROR_MSG_TYPE, SUCCESS_MSG_TYPE } from "../../../constants/messages";
import VendorDetailCard from "./VendorDetailCard";

/* ================= DATE RULES ================= */
const disablePastDates = (current) =>
  current && current < dayjs().startOf("day");

/**
 * Quick Edit Vendor modal.
 *
 * A focused editor for the common vendor settings (CHM, website, status,
 * plan/setup amounts, billing dates, reminders, sponsored/rating and auto
 * bill generation). Saves through vendors/update (EDIT_VENDOR). Super admins
 * get relaxed date rules.
 *
 * Props:
 *  - open, onClose, vendorId   modal control + which vendor to load
 *  - onSaved                   called after a successful save (refresh list)
 */
function QuickEditVendor({ open, onClose, vendorId, onSaved }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);
  // Once a (non-cancelled) subscription bill exists, the live/billing start
  // dates are locked — changing them retroactively would be inconsistent.
  const [billingDatesLocked, setBillingDatesLocked] = useState(false);

  useEffect(() => {
    if (open && vendorId) fetchVendor(vendorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vendorId]);

  const fetchVendor = (id) => {
    setLoading(true);
    postAPI(
      VENDOR_BY_ID,
      { id },
      (res) => {
        if (res.status) {
          const v = res.data;
          setVendor(v);

          // Lock live/billing-start dates if a non-cancelled subscription
          // bill has already been generated for this vendor.
          const hasSubscriptionBill = (v.bills || []).some(
            (b) => b.bill_type === "subscription" && b.bill_status !== "cancelled",
          );
          setBillingDatesLocked(hasSubscriptionBill);

          form.setFieldsValue({
            chm_active: v.chm_active ? 1 : 0,
            chm_hotel_code: v.chm_hotel_code,
            url: v.url,
            status: v.status,
            document_approved: v.document_approved ? 1 : 0,
            active_plan_amount:
              v.active_plan_amount !== null &&
              v.active_plan_amount !== undefined
                ? parseInt(v.active_plan_amount)
                : null,
            setup_charage_amount:
              v.setup_charage_amount !== null &&
              v.setup_charage_amount !== undefined
                ? parseInt(v.setup_charage_amount)
                : null,
            expected_live_date: v.expected_live_date
              ? dayjs(v.expected_live_date)
              : null,
            billing_start_date: v.billing_start_date
              ? dayjs(v.billing_start_date)
              : null,
            next_billing_date: v.next_billing_date
              ? dayjs(v.next_billing_date)
              : null,
            bill_due_reminder_in_day: v.bill_due_reminder_in_day,
            bill_due_block_system_in_day: v.bill_due_block_system_in_day,
            is_sponserd: v.is_sponserd ? 1 : 0,
            google_ratting: v.google_ratting,
            auto_bill_generation: v.auto_bill_generation ? 1 : 0,
          });
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  const onFinish = (values) => {
    setSaving(true);
    const payload = {
      id: vendorId,
      ...values,
      expected_live_date: values.expected_live_date
        ? values.expected_live_date.format("YYYY-MM-DD")
        : null,
      billing_start_date: values.billing_start_date
        ? values.billing_start_date.format("YYYY-MM-DD")
        : null,
      next_billing_date: values.next_billing_date
        ? values.next_billing_date.format("YYYY-MM-DD")
        : null,
    };

    postAPI(
      EDIT_VENDOR,
      payload,
      (res) => {
        setSaving(false);
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          if (onSaved) onSaved(res);
          onClose();
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
      () => {
        setSaving(false);
        displayMessage(ERROR_MSG_TYPE, "Failed to update vendor");
      },
    );
  };

  // Super admins may pick any date; others cannot pick past dates.
  const dateRule = isSuperAdmin() ? undefined : disablePastDates;

  return (
    <Modal
      open={open}
      title="Quick Edit Vendor"
      width={1400}
      destroyOnHidden
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={() => form.submit()}
        >
          Save Changes
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        {vendor && (
          <div style={{ marginBottom: 18 }}>
            <VendorDetailCard vendor={vendor} />
          </div>
        )}

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={[16, 0]}>
            <Col {...colProps}>
              <Form.Item name="chm_active" label="CHM Active">
                <Select placeholder="Select" options={YES_NO_OPTION} />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="chm_hotel_code"
                label="CHM Hotel Code"
                tooltip="Channel Manager Hotel Code"
              >
                <Input placeholder="CHM Hotel Code" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="url"
                label="Website URL"
                rules={[
                  { pattern: urlRegex, message: "Enter valid website URL" },
                ]}
              >
                <Input placeholder="https://hotelnxt.com" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item name="status" label="Status">
                <Select placeholder="Select" options={ACTIVE_INACTIVE_OPTION} />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item name="document_approved" label="Document Approved">
                <Select placeholder="Select" options={YES_NO_OPTION} />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="active_plan_amount"
                label="Plan Amount"
                rules={[
                  { pattern: /^\d+$/, message: "Only numeric value allowed" },
                ]}
              >
                <Input placeholder="Plan Amount" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="setup_charage_amount"
                label="Setup Charge Amount"
                rules={[
                  { pattern: /^\d+$/, message: "Only numeric value allowed" },
                ]}
              >
                <Input placeholder="Setup Charge Amount" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="expected_live_date"
                label="Expected Live Date"
                tooltip={
                  billingDatesLocked
                    ? "Locked: a subscription bill has already been generated"
                    : undefined
                }
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={dateRule}
                  format="YYYY-MM-DD"
                  disabled={billingDatesLocked}
                />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="billing_start_date"
                label="Billing Start Date"
                tooltip={
                  billingDatesLocked
                    ? "Locked: a subscription bill has already been generated"
                    : undefined
                }
              >
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={dateRule}
                  format="YYYY-MM-DD"
                  disabled={billingDatesLocked}
                />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item name="next_billing_date" label="Next Billing Date">
                <DatePicker
                  style={{ width: "100%" }}
                  disabledDate={dateRule}
                  format="YYYY-MM-DD"
                />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="bill_due_reminder_in_day"
                label="Reminder Days"
                tooltip="After bill generation, remind in the software"
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Only positive numbers allowed",
                  },
                ]}
              >
                <Input placeholder="Reminder Days" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="bill_due_block_system_in_day"
                label="Block System Days"
                tooltip="After bill generation, block the software when overdue"
                rules={[
                  {
                    pattern: /^\d+$/,
                    message: "Only positive numbers allowed",
                  },
                ]}
              >
                <Input placeholder="Block System Days" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item name="is_sponserd" label="Sponserd">
                <Select placeholder="Select" options={YES_NO_OPTION} />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="google_ratting"
                label="Google Ratting"
                tooltip="Google rating like: 4.2"
                rules={[
                  {
                    pattern: /^\d+(\.\d{1,2})?$/,
                    message: "Only numeric (up to 2 decimals)",
                  },
                ]}
              >
                <Input placeholder="e.g. 4.2" />
              </Form.Item>
            </Col>

            <Col {...colProps}>
              <Form.Item
                name="auto_bill_generation"
                label="Auto Bill Generation"
              >
                <Select placeholder="Select" options={YES_NO_OPTION} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}

export default QuickEditVendor;
